import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { styled } from "@pigment-css/react";
import { ChevronLeft, PencilLine, Trash } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { type Customer, Note, type Todo } from "@acto/taskpane/core/domain";
import { TodoStatus } from "@acto/gql/graphql";
import { useNoteTodos } from "@acto/taskpane/core/data-access/todos/useNoteTodos";
import { useUpdateTodos } from "@acto/taskpane/core/data-access/todos/useUpdateTodos";
import { useDeleteNote } from "@acto/taskpane/core/data-access/notes/useDeleteNote";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import { useCreateRecap } from "@acto/taskpane/field-sales-2/data-access/recap";
import { useRecapEditingEnabled } from "@acto/taskpane/field-sales-2/data-access/useRecapEditingEnabled";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";
import { formatDayMonthYear } from "@acto/taskpane/field-sales-2/utils/format";
import { usePageEnterAnimation } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { TopNav } from "@acto/taskpane/field-sales-2/components/patterns/TopNav";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { ActionPillButton, ActionPillMenuButton } from "@acto/taskpane/field-sales-2/components/primitives/ActionPill";
import { HeaderActionPill } from "@acto/taskpane/field-sales-2/components/primitives/HeaderActionPill";
import { KebabIcon } from "@acto/taskpane/field-sales-2/components/primitives/KebabIcon";
import { TitlePill } from "@acto/taskpane/field-sales-2/components/primitives/TitlePill";
import { useToast, UndoToast, UndoToastSlot } from "@acto/taskpane/field-sales-2/components/primitives/Toast";
import { useUndoBatch } from "@acto/taskpane/field-sales-2/utils/hooks/useUndoBatch";
import { RecapReadView } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapReadView";
import { TodoEditSheet } from "@acto/taskpane/field-sales-2/components/patterns/TodoEditSheet";
import { useOptimisticTodoReassignment } from "@acto/taskpane/field-sales-2/pages/useOptimisticTodoReassignment";
import { RecapAnimationScreen } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapCreationFlow/RecapAnimationScreen";
import { RecapEditSummaryScreen } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapCreationFlow/RecapEditing";

// Fetch open AND completed todos (the backend `noteTodos` filter defaults to OPEN only) so completed
// todos stay visible under the "Done" bucket instead of vanishing once checked off. Module-level
// constant → stable reference for the `useNoteTodos` filter memo.
const RECAP_TODO_STATUSES = [TodoStatus.Open, TodoStatus.Completed];

const Page = styled("main")(({ theme }) => ({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  background: theme.fieldSalesTwo.colors.background,
}));

const Body = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[32]}`,
}));

interface RecapStaticViewProps {
  /** The recap note to display. Carries the summary; todos are fetched note-keyed. */
  recap: Note;
  /** The customer this recap belongs to; supplies the edit-view pill's name + reference. */
  customer?: Customer;
  onClose: () => void;
  /** Refetch the recaps list after an edit so it reflects the regenerated summary. */
  onSaved?: () => void;
  /** Called after a successful delete; defaults to onClose. */
  onDeleted?: () => void;
}

/** view: read-only detail; editing: transcript editor; saving: re-summarize in flight. */
type Mode = "view" | "editing" | "saving";

/**
 * Detail view of a past recap: the summary + todos as the creation flow's final screen, built on
 * the shared `RecapReadView` core. Todos ARE completable here (passes `onCompleteTodo`): tapping a
 * checkbox checks it in place, and after a short undo window the COMPLETED mutation commits. The
 * pencil opens a transcript editor (`RecapEditSummaryScreen`); saving re-commits the content and
 * re-runs the AI summarize (same path as the creation flow), showing the animation meanwhile.
 */
export function RecapStaticView({ recap, customer, onClose, onSaved, onDeleted }: RecapStaticViewProps) {
  const t = useTranslations("fieldSalesTwo.recaps");
  const { locale } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);
  usePageEnterAnimation(contentRef);

  // The recap edited in place: seeded from the prop, overwritten with the saved content + freshly
  // regenerated summary so the detail reflects the edit without a parent round-trip. The id is
  // stable (saveNote upserts the same note), so the note-keyed todo read below keeps working.
  const [note, setNote] = useState(recap);
  const [mode, setMode] = useState<Mode>("view");
  const [pendingContent, setPendingContent] = useState<string | null>(null);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const editingEnabled = useRecapEditingEnabled();
  // The saveNote response predates the re-summarize, so its summary is stale; refetch the recaps
  // once the fresh summary lands server-side, else the list and a reopened detail read the old one.
  const recapApi = useCreateRecap({ onRefetchNotes: onSaved });
  const track = useTrack();

  const { data: todos, refetch: refetchTodos } = useNoteTodos({
    noteId: note.id,
    processedStatuses: RECAP_TODO_STATUSES,
  });
  const { updateTodos } = useUpdateTodos();
  const { deleteNote } = useDeleteNote();

  const { showToast } = useToast();

  const [committed, setCommitted] = useState<Set<string>>(() => new Set());

  const completeTodo = useCallback(
    async (id: string) => {
      const updated = await updateTodos({ input: { id, processedStatus: TodoStatus.Completed } });
      if (updated) return;
      // `useUpdateTodos` swallows errors and resolves null — roll the id out of the overlay so it
      // reverts to unchecked instead of lingering as a "done" ghost.
      setCommitted((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [updateTodos],
  );

  const completions = useUndoBatch<string>((batch) => {
    setCommitted((prev) => new Set([...prev, ...batch.keys()]));
    batch.forEach((id) => void completeTodo(id));
  });

  const { pending, schedule: scheduleCompletion, flush: flushCompletions } = completions;

  const handleComplete = useCallback((id: string) => scheduleCompletion(id, id), [scheduleCompletion]);

  const {
    assigneeFor,
    handleReassign,
    handleUndo: handleReassignUndo,
    pendingCount: reassignPendingCount,
  } = useOptimisticTodoReassignment(updateTodos, todos ?? [], flushCompletions);

  const shownTodos = useMemo(
    () => (todos ?? []).map((todo) => todo.withAssignee(assigneeFor(todo))),
    [todos, assigneeFor],
  );

  // The completion banner unmounts with this screen, so its grace window ends here rather than
  // staying undoable with nothing on screen offering it.
  useEffect(() => {
    if (mode === "view") return;
    flushCompletions();
  }, [mode, flushCompletions]);

  // Drop overlay entries once the refetch reports them completed server-side (server authoritative).
  useEffect(() => {
    setCommitted((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set(prev);
      (todos ?? []).forEach((todo) => {
        if (todo.isCompleted() && next.has(todo.id)) {
          next.delete(todo.id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [todos]);

  // Persist an edited transcript: re-commit (saveNote upserts by meeting) and re-run the summarize.
  // The completion effect below folds in the fresh summary once the stream succeeds. A failed save
  // persisted nothing and reopens the editor on the text, unless the user has already left.
  const handleSaveContent = async (content: string) => {
    setPendingContent(content);
    setMode("saving");
    const saved = await recapApi.commit({ clientId: note.clientId, meetingId: note.meetingId ?? undefined, content });
    if (!saved) {
      setMode((current) => (current === "saving" ? "editing" : current));
      showToast({ message: t("edit.saveFailed"), tone: "destructive" });
      return;
    }
    // The stored summary was generated from the content this save replaced, so it is dropped with
    // it. The success path below writes the fresh one.
    setNote(
      (prev) =>
        new Note(
          prev.id,
          prev.clientId,
          saved.content,
          prev.createdAt,
          dayjs(saved.updatedAt),
          prev.meetingId,
          prev.title,
          null,
          prev.openTodosCount,
        ),
    );
    track(AnalyticsEvents.RECAP_EDITED, { recapId: note.id, customerId: note.clientId });
  };

  // On success hand off to onDeleted; on failure stay on the detail and surface an error toast.
  const handleDelete = async () => {
    if (await deleteNote(note.id)) (onDeleted ?? onClose)();
    else showToast({ message: t("edit.deleteFailed"), tone: "destructive" });
  };

  // Resolve the summarize: a failure still returns to the detail, since the content is saved by
  // then and the body falls back to it.
  useEffect(() => {
    if (mode !== "saving") return;

    if (recapApi.summarizeError) {
      recapApi.reset();
      setPendingContent(null);
      setMode("view");
      showToast({ message: t("animation.failed"), tone: "destructive" });
      return;
    }

    if (!recapApi.isDone || !recapApi.note) return;
    const saved = recapApi.note;
    setNote(
      (prev) =>
        new Note(
          prev.id,
          prev.clientId,
          saved.content,
          prev.createdAt,
          dayjs(saved.updatedAt),
          prev.meetingId,
          prev.title,
          recapApi.summaryText ?? saved.summary,
          saved.openTodosCount ?? prev.openTodosCount,
        ),
    );
    recapApi.reset();
    setPendingContent(null);
    refetchTodos?.();
    setMode("view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, recapApi.isDone, recapApi.summarizeError]);

  // Pending ids stay in the open bucket, checked in place during the undo window; committed ids move
  // to the "Done" bucket (the RecapReadView split treats them as completed until the refetch lands).
  const pendingIds = useMemo(() => [...pending.keys()], [pending]);
  const committedIds = useMemo(() => [...committed], [committed]);

  // Prefer the AI summary; fall back to the raw note content when no summary exists yet (older
  // recaps, or summarization that hasn't run) so the body is never empty.
  const body = note.summary?.trim() ? note.summary : note.content;

  const customerName = customer?.name ?? "";
  const customerReference = customer?.externalReference ?? "";

  if (mode === "saving") {
    return (
      <RecapAnimationScreen
        customerName={customerName}
        isSummarizing={recapApi.isSummarizing}
        onClose={() => {
          recapApi.reset();
          setMode("view");
        }}
      />
    );
  }

  if (mode === "editing") {
    return (
      <RecapEditSummaryScreen
        customerName={customerName}
        customerReference={customerReference}
        aiSummary={note.summary ?? ""}
        content={note.content}
        initialDraft={pendingContent ?? undefined}
        onBack={() => {
          setPendingContent(null);
          setMode("view");
        }}
        onSave={handleSaveContent}
      />
    );
  }

  return (
    <Page>
      <TopNav
        titlePlacement="leading"
        leading={
          <HeaderIconButton type="button" aria-label={t("a11y.back")} onClick={onClose}>
            <ChevronLeft width={18} height={18} />
          </HeaderIconButton>
        }
        title={<TitlePill>{formatDayMonthYear(note.createdAt.format("YYYY-MM-DD"), locale)}</TitlePill>}
        trailing={
          editingEnabled ? (
            <HeaderActionPill variant="action">
              {/* A recap is re-saved by upserting its meeting; a meeting-less note has no editable path. */}
              {note.meetingId && (
                <ActionPillButton aria-label={t("a11y.edit")} onClick={() => setMode("editing")}>
                  <PencilLine />
                </ActionPillButton>
              )}
              <MenuPopover
                trigger={
                  <ActionPillMenuButton aria-label={t("a11y.options")}>
                    <KebabIcon />
                  </ActionPillMenuButton>
                }
                ariaLabel={t("edit.options")}
                items={[
                  {
                    label: t("edit.deleteRecap"),
                    icon: <Trash aria-hidden="true" />,
                    tone: "destructive",
                    onSelect: handleDelete,
                  },
                ]}
              />
            </HeaderActionPill>
          ) : undefined
        }
      />

      <Body ref={contentRef}>
        <RecapReadView
          summary={body}
          todos={shownTodos}
          checkedTodoIds={pendingIds}
          completedTodoIds={committedIds}
          onSelectTodo={(id) => setEditingTodo(shownTodos.find((todo) => todo.id === id) ?? null)}
          onCompleteTodo={handleComplete}
          completeTodoLabel={t("summary.completeAriaLabel")}
          groupByAssignee
        />
      </Body>

      <UndoToastSlot>
        {pending.size > 0 && (
          <UndoToast
            message={t("summary.completedToast", { count: pending.size })}
            undoLabel={t("summary.undo")}
            onUndo={completions.undo}
          />
        )}
        {reassignPendingCount > 0 && (
          <UndoToast
            message={t("summary.reassignedToast", { count: reassignPendingCount })}
            undoLabel={t("summary.undo")}
            onUndo={handleReassignUndo}
          />
        )}
      </UndoToastSlot>

      <TodoEditSheet
        todo={editingTodo}
        isOpen={editingTodo !== null}
        onOpenChange={(open) => {
          if (!open) setEditingTodo(null);
        }}
        onReassign={handleReassign}
      />
    </Page>
  );
}
